// src/pages/ProgramsPage.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaHome, 
  FaChevronRight, 
  FaFilter, 
  FaSearch, 
  FaTimes
} from 'react-icons/fa';
import Section from '../components/common/Section';
import Heading from '../components/common/Heading';
import Button from '../components/common/Button';
import COURSES_DATA from '../data/coursesdata';

const ProgramsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const categoryParam = queryParams.get('category');
  
  // Convert our course data structure into a flat array of all courses
  const allPrograms = Object.values(COURSES_DATA).flatMap(category => category.courses);
  
  const [activeFilter, setActiveFilter] = useState(categoryParam || 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPrograms, setFilteredPrograms] = useState(allPrograms);
  const [isMobileFilterVisible, setIsMobileFilterVisible] = useState(false);
  
  // Update activeFilter when URL query parameter changes
  useEffect(() => {
    if (categoryParam) {
      setActiveFilter(categoryParam);
    } else {
      setActiveFilter('all');
    }
  }, [categoryParam]);
  
  // Filter programs based on category and search term
  useEffect(() => {
    const results = allPrograms.filter(program => {
      const matchesCategory = activeFilter === 'all' || program.category === activeFilter;
      const matchesSearch = program.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          program.shortDesc.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesCategory && matchesSearch;
    });
    
    setFilteredPrograms(results);
  }, [activeFilter, searchTerm]);
  
  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Handle category filter change and update URL
  const handleFilterChange = (category) => {
    setActiveFilter(category);
    
    // Update URL with query parameter (or remove it if 'all')
    if (category === 'all') {
      navigate('/programs');
    } else {
      navigate(`/programs?category=${category}`);
    }
  };
  
  // Define categories
  const categories = [
    { id: 'all', name: 'All Programs' },
    { id: 'green', name: 'Green Skills & Climate' },
    { id: 'digital', name: 'Digital Literacy' },
    { id: 'engineering', name: 'Engineering & Design' },
    { id: 'entrepreneurship', name: 'Entrepreneurship & Mentorship' }
  ];
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  
  return (
    <>
      <Helmet>
        <title>Our Programs - Afrovate Tech Hub SL</title>
        <meta name="description" content="Explore Afrovate Tech Hub's range of digital skills training programs designed to empower youth with marketable skills." />
      </Helmet>
      
      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-16 overflow-hidden"
        style={{
          backgroundImage: 'url(/images/programs/programs-hero-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Dark overlay for better text visibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-dark to-primary opacity-75"></div>
        
        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          {/* Breadcrumbs */}
          <div className="flex items-center justify-center mb-8 text-white text-sm">
            <Link to="/" className="flex items-center hover:text-secondary transition-colors">
              <FaHome className="mr-2" />
              Home
            </Link>
            <FaChevronRight className="mx-2 text-xs text-gray-300" />
            <span className="text-secondary font-medium">Programs</span>
            {activeFilter !== 'all' && (
              <>
                <FaChevronRight className="mx-2 text-xs text-gray-300" />
                <span className="text-white font-medium">
                  {categories.find(cat => cat.id === activeFilter)?.name}
                </span>
              </>
            )}
          </div>
          
          <div className="text-center max-w-3xl mx-auto">
            <motion.h1 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-4xl md:text-5xl font-bold text-white mb-6"
            >
              {activeFilter === 'all' 
                ? 'Our Training Programs' 
                : categories.find(cat => cat.id === activeFilter)?.name}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-xl text-white text-opacity-90 mb-8"
            >
              Practical, hands-on training in cutting-edge digital skills for the modern workforce. 
              Our programs are designed to enhance your employability and entrepreneurial capabilities.
            </motion.p>
            
            {/* Search Bar */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="relative max-w-xl mx-auto"
            >
              <input
                type="text"
                placeholder="Search programs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-3 px-6 pr-12 bg-white bg-opacity-95 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-secondary"
              />
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500">
                {searchTerm ? (
                  <button onClick={() => setSearchTerm('')} className="hover:text-primary">
                    <FaTimes />
                  </button>
                ) : (
                  <FaSearch />
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Filter Bar - Desktop */}
      <div className="sticky top-16 z-20 bg-white shadow-md border-b border-gray-200 hidden md:block">
        <div className="container mx-auto px-4">
          <div className="flex justify-center py-4 space-x-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleFilterChange(category.id)}
                className={`px-4 py-2 rounded-full font-medium transition-colors ${
                  activeFilter === category.id 
                    ? 'bg-primary text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Filter Button - Mobile */}
      <div className="fixed bottom-6 right-6 z-30 md:hidden">
        <button
          onClick={() => setIsMobileFilterVisible(!isMobileFilterVisible)}
          className="bg-primary text-white p-4 rounded-full shadow-lg flex items-center justify-center"
          aria-label="Toggle filters"
        >
          <FaFilter />
        </button>
      </div>
      
      {/* Mobile Filter Panel */}
      <AnimatePresence>
        {isMobileFilterVisible && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-0 left-0 right-0 bg-white rounded-t-xl shadow-lg p-6 z-30 md:hidden"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-primary-dark">Filter Programs</h3>
              <button
                onClick={() => setIsMobileFilterVisible(false)}
                className="text-gray-500 hover:text-primary"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="space-y-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => {
                    handleFilterChange(category.id);
                    setIsMobileFilterVisible(false);
                  }}
                  className={`w-full px-4 py-3 rounded-lg font-medium text-left transition-colors ${
                    activeFilter === category.id 
                      ? 'bg-primary text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Programs Grid */}
      <Section background="white" padding="lg">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Heading level={2} className="mb-2">
              {activeFilter === 'all' ? 'All Programs' : `${categories.find(c => c.id === activeFilter)?.name}`}
            </Heading>
            <p className="text-gray-600">
              {filteredPrograms.length} program{filteredPrograms.length !== 1 ? 's' : ''} found
              {searchTerm && ` matching "${searchTerm}"`}
              {activeFilter !== 'all' && ` in ${categories.find(c => c.id === activeFilter)?.name}`}
            </p>
          </div>
          
          {(activeFilter !== 'all' || searchTerm) && (
            <Button
              variant="text"
              onClick={() => {
                setSearchTerm('');
                handleFilterChange('all');
              }}
              className="text-primary hover:text-primary-dark"
            >
              Clear Filters
            </Button>
          )}
        </div>
        
        {filteredPrograms.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <div className="text-gray-400 text-5xl mb-4">
              <FaSearch className="mx-auto" />
            </div>
            <h3 className="text-xl font-bold text-gray-700 mb-2">No Programs Found</h3>
            <p className="text-gray-600 mb-6">
              We couldn't find any programs matching your criteria.
            </p>
            <Button
              variant="primary"
              onClick={() => {
                setSearchTerm('');
                handleFilterChange('all');
              }}
            >
              View All Programs
            </Button>
          </div>
        ) : (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {filteredPrograms.map((program) => (
              <motion.div
                key={program.id}
                variants={itemVariants}
                className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
              >
                <div className="relative h-48 bg-gradient-to-r from-primary-dark to-primary overflow-hidden">
                  {program.image ? (
                    <img
                      src={program.image}
                      alt={program.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/images/programs/programs-hero-bg.jpg';
                      }}
                    />
                  ) : (
                    <div className="h-full flex items-center justify-center text-white">
                      <div className="p-6 text-5xl">
                        {program.icon}
                      </div>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/70 via-transparent to-transparent"></div>
                  <div className="absolute bottom-4 left-4 h-12 w-12 rounded-full bg-white/90 text-primary flex items-center justify-center text-2xl shadow-md">
                    {program.icon}
                  </div>
                </div>
                
                <div className="p-6">
                  <span className="inline-block bg-blue-100 text-primary-dark text-xs font-medium rounded-full px-2 py-1 mb-3">
                    {program.category.toUpperCase()}
                  </span>
                  <h3 className="text-xl font-bold text-primary-dark mb-3">{program.title}</h3>
                  <p className="text-gray-600 mb-6">{program.shortDesc}</p>
                  
                 
                  
                  <Link
                    to={`/programs/${program.id}`}
                    className="inline-flex items-center justify-center w-full bg-primary hover:bg-primary-dark text-white font-medium py-2 px-4 rounded-md transition-colors"
                  >
                    View Program Details
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </Section>
      
      {/* Program Categories Section */}
      <Section background="gray" padding="lg">
        <div className="text-center mb-12">
          <Heading level={2} align="center" className="mb-4" withAccent>
            Skills Training Categories
          </Heading>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Our training programs are organized by category to help you find the right fit for your career goals
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {Object.values(COURSES_DATA).map((category) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
            >
              <div className={`${
                category.id === 'green' ? 'bg-green-500' :
                category.id === 'digital' ? 'bg-blue-500' :
                category.id === 'engineering' ? 'bg-purple-500' :
                'bg-yellow-500'
              } p-6 flex justify-center`}>
                <div className="h-20 w-20 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                  {category.icon}
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-primary-dark mb-2">{category.title}</h3>
                <p className="text-gray-600 mb-4">{category.summary}</p>
                <div className="text-sm text-gray-500 mb-4">
                  {category.courses.length} program{category.courses.length !== 1 ? 's' : ''} available
                </div>
                
                <button
                  onClick={() => handleFilterChange(category.id)}
                  className="inline-flex items-center text-primary hover:text-primary-dark font-medium"
                >
                  Browse {category.title} Programs
                  <FaChevronRight className="ml-2 h-3 w-3" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>
      
      {/* Application Process Section */}
      <Section background="white" padding="lg">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <Heading level={2} withAccent className="mb-6">
              Application Process
            </Heading>
            
            <p className="text-gray-700 mb-8">
              Joining a Afrovate Tech Hub training program is straightforward. Follow these simple steps to begin your
              journey toward acquiring marketable digital skills:
            </p>
            
            <div className="space-y-6">
              {[
                {
                  step: 1,
                  title: 'Browse and Select a Program',
                  description: 'Explore our program offerings and choose one that aligns with your career goals and interests.'
                },
                {
                  step: 2,
                  title: 'Complete the Application',
                  description: 'Fill out our simple online application form with your personal information and program selection.'
                },
                {
                  step: 3,
                  title: 'Receive Confirmation',
                  description: `Once your application is submitted, you'll receive a confirmation email with next steps.`
                },
                {
                  step: 4,
                  title: 'Complete Registration',
                  description: 'Finalize your registration by paying the program fee and submitting any required documents.'
                },
                {
                  step: 5,
                  title: 'Begin Your Training',
                  description: 'Attend orientation and start your journey to acquiring valuable digital skills.'
                }
              ].map((item) => (
                <div key={item.step} className="flex">
                  <div className="h-10 w-10 rounded-full bg-primary-dark text-white flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="font-bold text-primary-dark mb-1">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-8">
              <Button 
                variant="primary" 
                size="lg" 
                to="/apply"
              >
                Apply Now
              </Button>
            </div>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="rounded-lg overflow-hidden shadow-xl"
          >
            <div className="aspect-w-16 aspect-h-9">
              <iframe
                src="https://www.youtube.com/embed/NL4OANDGkP4"
                title="Afrovate Tech Hub Application Process"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full rounded-lg"
                style={{ aspectRatio: '16/9', height: '400px' }}
              ></iframe>
            </div>
          </motion.div>
        </div>
      </Section>
      
      {/* Call to Action */}
      <Section background="gradient-primary" padding="lg">
        <div className="text-center max-w-3xl mx-auto">
          <Heading level={2} color="white" align="center" className="mb-6">
            Ready to Build Your Future?
          </Heading>
          <p className="text-xl text-white text-opacity-90 mb-10">
            Invest in your future by acquiring digital skills that will set you apart in the job market.
            Join Afrovate Tech Hub's growing community of successful professionals.
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <Button 
              variant="secondary" 
              size="lg" 
              to="/apply"
            >
              Apply for a Program
            </Button>
            <Button 
              variant="light" 
              size="lg" 
              to="/contact"
            >
              Need Guidance? Contact Us
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
};

export default ProgramsPage;
